import { apiClient } from './apiClient';
import type {
  InvitationDetails,
  AgentRegistrationRequest,
  AgentRegistrationResponse,
  BusinessAgent,
  PendingInvitation,
} from '../types/agent';

// API response types (wrapped format)
interface GetBusinessAgentsResponse {
  success: boolean;
  message: string;
  agents: BusinessAgent[];
}

interface GetPendingInvitationsResponse {
  success: boolean;
  message: string;
  invitations: PendingInvitation[];
}

export const agentService = {
  /**
   * Get invitation details by token
   * @param token - Invitation token from URL
   */
  async getInvitationDetails(token: string): Promise<InvitationDetails> {
    return apiClient.get<InvitationDetails>(`/agent/invitation/${token}`);
  },

  /**
   * Register an agent with invitation token
   * @param data - Agent registration data including invitation token
   */
  async registerAgent(
    data: AgentRegistrationRequest
  ): Promise<AgentRegistrationResponse> {
    return apiClient.post<AgentRegistrationResponse>(
      '/auth/register/agent',
      data
    );
  },

  /**
   * Get all agents for a business profile
   */
  async getBusinessAgents(businessProfileId: string, authToken: string): Promise<BusinessAgent[]> {
    const response = await apiClient.get<GetBusinessAgentsResponse>(`/agent/business-profile/${businessProfileId}`, authToken);
    return response.agents || [];
  },

  /**
   * Get agents for current user's business
   */
  async getMyBusinessAgents(authToken: string): Promise<BusinessAgent[]> {
    return apiClient.get<BusinessAgent[]>('/agent/my-business', authToken);
  },

  /**
   * Invite a new agent
   */
  async inviteAgent(
    email: string,
    businessProfileId: string | undefined,
    authToken: string
  ): Promise<{ invitationId: string; message: string }> {
    const url = businessProfileId
      ? `/agent/invite?businessProfileId=${businessProfileId}`
      : '/agent/invite';
    return apiClient.post<{ invitationId: string; message: string }>(url, { email }, authToken);
  },

  /**
   * Remove an agent from business
   */
  async removeAgent(
    businessProfileId: string,
    agentUserId: string,
    authToken: string
  ): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `/agent/business-profile/${businessProfileId}/agent/${agentUserId}`,
      authToken
    );
  },

  /**
   * Get pending invitations for a business profile
   */
  async getPendingInvitations(businessProfileId: string, authToken: string): Promise<PendingInvitation[]> {
    const response = await apiClient.get<GetPendingInvitationsResponse>(
      `/agent/invitations/business-profile/${businessProfileId}`,
      authToken
    );
    return response.invitations || [];
  },

  /**
   * Cancel a pending invitation
   */
  async cancelInvitation(invitationId: string, authToken: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/agent/invitations/${invitationId}`, authToken);
  },
};
